import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import TppModalGuide from '../../common/TppModalGuide';
import { tppMutationError } from '../../../utils/tppApiError';
import { TPP_MODAL_CONTENT_CLASS, TppBtnSpinner } from './tppUiShared';
import type { TppPoolSettingsSeed, TppPoolSettingsSubmitPayload } from './tppDataModalTypes';

type TppPoolSettingsModalProps = {
  closeLabel: string;
  seed: TppPoolSettingsSeed | undefined;
  show: boolean;
  onHide: () => void;
  pending: boolean;
  error: unknown;
  onSubmit: (payload: TppPoolSettingsSubmitPayload) => void;
};

/** 残高表示に効く項目だけ編集。Zip 互換の他フィールドは seed を維持 */
export default function TppPoolSettingsModal({
  closeLabel,
  seed,
  show,
  onHide,
  pending,
  error,
  onSubmit,
}: TppPoolSettingsModalProps) {
  const { t } = useTranslation();

  const [dispPrefix, setDispPrefix] = useState('');
  const [dispSuffix, setDispSuffix] = useState('');
  const [fractionDigits, setFractionDigits] = useState(0);

  useEffect(() => {
    if (!show || !seed) return;
    setDispPrefix(seed.displayPrefix);
    setDispSuffix(seed.displaySuffix);
    setFractionDigits(seed.fractionDigits);
  }, [show, seed]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      scrollable
      contentClassName={TPP_MODAL_CONTENT_CLASS}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (pending || !seed) return;
          onSubmit({
            displayPrefix: dispPrefix,
            displaySuffix: dispSuffix,
            isoCurrencyCode: seed.isoCurrencyCode,
            fractionDigits,
            inputRoundingMode: seed.inputRoundingMode,
            displayRoundingMode: seed.displayRoundingMode,
          });
        }}
      >
        <Modal.Header closeButton closeLabel={closeLabel}>
          <Modal.Title>{t('tpp.pool_settings_modal_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TppModalGuide
            collapsible
            introKey="tpp.pool_settings_help_intro"
            stepKeys={[
              'tpp.pool_settings_help_step1',
              'tpp.pool_settings_help_step2',
              'tpp.pool_settings_help_step3',
            ]}
            noteKey="tpp.pool_settings_help_note"
          />
          <Row className="g-2 mb-3">
            <Col md={6}>
              <Form.Label className="small" htmlFor="tpp-settings-prefix">
                {t('tpp.display_prefix')}
              </Form.Label>
              <Form.Control
                id="tpp-settings-prefix"
                value={dispPrefix}
                onChange={(e) => setDispPrefix(e.target.value)}
              />
              <Form.Text muted>{t('tpp.pool_settings_prefix_hint')}</Form.Text>
            </Col>
            <Col md={6}>
              <Form.Label className="small" htmlFor="tpp-settings-suffix">
                {t('tpp.display_suffix')}
              </Form.Label>
              <Form.Control
                id="tpp-settings-suffix"
                value={dispSuffix}
                onChange={(e) => setDispSuffix(e.target.value)}
              />
              <Form.Text muted>
                {t('tpp.pool_settings_suffix_hint')}
              </Form.Text>
            </Col>
          </Row>
          <Row className="g-2">
            <Col md={6}>
              <Form.Label className="small" htmlFor="tpp-settings-fraction">
                {t('tpp.fraction_digits')}
              </Form.Label>
              <Form.Control
                id="tpp-settings-fraction"
                type="number"
                min={0}
                max={4}
                value={fractionDigits}
                onChange={(e) =>
                  setFractionDigits(Math.min(4, Math.max(0, Number.parseInt(e.target.value, 10) || 0)))
                }
              />
              <Form.Text muted>
                {t('tpp.pool_settings_fraction_hint')}
              </Form.Text>
            </Col>
          </Row>
          {error != null && (
            <Alert variant="danger" className="mt-3 mb-0 py-2 small">
              {tppMutationError(error, t)}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onHide}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={pending || !seed}>
            <TppBtnSpinner show={pending} />
            {t('common.save')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
